import {
  getContentProps,
  getImageUrl,
  I_ContentMeta,
} from '../../common/state/apiGet';
import {
  DEFAULT_DATE_FORMAT,
  formatFromServerDate,
  getDuration,
  SECONDS_OF_AN_HOUR,
  SECONDS_OF_A_DAY,
} from '../../common/util/date';
import {IExpireDate} from '../../commonSub/type';
import {
  IPrize,
  IResult,
  ILuckydrawReward,
  IEnterInfo,
  ILuckydrawTheme,
} from '../type';

const LUCKYDRAW_THEME_MAP: {[key in I_LuckydrawTheme]: ILuckydrawTheme} = {
  SLOT_MACHINE: 'slotMachine',
  GIFT_BOX: 'box',
};

function getPrize(prize: I_Prize): IPrize {
  let expireDate: string | IExpireDate | undefined;

  if (prize.expiration) {
    if (prize.expiration.type === 'DATE') {
      expireDate = formatFromServerDate(
        prize.expiration.value,
        DEFAULT_DATE_FORMAT
      );
    } else {
      const {totalSeconds, isoDuration} = getDuration(prize.expiration.value);
      let days = 0;
      let hours = 0;

      if (totalSeconds / SECONDS_OF_A_DAY >= 1) {
        days = Math.floor(totalSeconds / SECONDS_OF_A_DAY);
      } else {
        hours = Math.floor(totalSeconds / SECONDS_OF_AN_HOUR);
      }

      expireDate = {days, hours, isoDuration};
    }
  }

  const _prize = {
    id: prize.id,
    type: prize.type,
    scrollImage: getImageUrl(prize.scrollImage),
    winningImage: getImageUrl(prize.winningImage, {hasAlpha: true}),
    receivedDate: formatFromServerDate(
      prize.receivedDateTime,
      DEFAULT_DATE_FORMAT
    ),
    expireDate,
    presentId: prize.item.id,
  };
  switch (prize.type) {
    case 'CASH':
      return {
        ..._prize,
        type: 'cash',
        cash: prize.quantity,
        name: String(prize.quantity),
      };
    case 'TICKET':
      return {
        ..._prize,
        type: 'ticket',
        ticketCount: prize.quantity,
        content: getContentProps(prize.item.relationship.content, [
          'id',
          'sid',
          'title',
          'isAdult',
        ]),
        name: prize.item.relationship.content.title,
      };
    case 'PRESENT':
      return {
        ..._prize,
        type: 'present',
        name: prize.item.name,
      };
  }
}

export function getLuckydrawReward(
  luckydrawReward: I_LuckydrawReward
): ILuckydrawReward {
  let list = luckydrawReward.luckyDraw.prizes;
  const theme = LUCKYDRAW_THEME_MAP[luckydrawReward.luckyDraw.theme];
  const isCashSlotMachine =
    theme === 'slotMachine' && list.every(prize => prize.type === 'CASH');

  /**
   * 슬롯머신은 내려온 아이템들을 9개까지 복사해서 동작시키는데(디자인을 맞추기 위해),그 중 5번 쨰 값이 가운데로 보이게된다.
   * 운영에서 총 3개 ~ 7개까지 설정할 수 있는데, 해당 값들이 9개로 복사가 됐을 때 가장 큰 값이 5번 쨰 위치하도록 한다.
   *
   * 3개: index 1
   * 4개: index 0
   * 5개 이상: index 4 로 가장 큰 값 이동
   */
  if (isCashSlotMachine) {
    const {length} = list;

    const maxCashItemindex = list.reduce(
      (maxIndex, prize, index, arr) =>
        prize.quantity > arr[maxIndex].quantity ? index : maxIndex,
      0
    );

    const swappedIndex = 5 % length === 0 ? 4 : (5 % length) - 1;
    const swappedItem = list[swappedIndex];

    list[swappedIndex] = list[maxCashItemindex];
    list[maxCashItemindex] = swappedItem;

    // eslint-disable-next-line no-self-assign
    list = list;
  }

  const _luckydrawReward = {
    rewardId: luckydrawReward.rewardId,
    isExpired: luckydrawReward.expired,
    luckyDraw: {
      id: luckydrawReward.luckyDraw.id,
      theme,
      title: luckydrawReward.luckyDraw.title,
      prizes: list.map(getPrize),
    },
    isCashSlotMachine,
  };

  return luckydrawReward.received
    ? {
        ..._luckydrawReward,
        isReceived: true,
        result: {
          prize: getPrize(luckydrawReward.result.prize),
          forms: luckydrawReward.result.forms,
        },
      }
    : {
        ..._luckydrawReward,
        isReceived: false,
      };
}

export function getResult(result: I_Result): IResult {
  return {
    prize: getPrize(result.prize),
  };
}

export function getEnterInfo(
  enterInfo: Omit<I_EnterInfo, 'prize'>
): Omit<IEnterInfo, 'prize'> {
  return {
    forms: enterInfo.result ? enterInfo.result.enteredForms : enterInfo.forms,
    isExpired: enterInfo.expired,
    isEntered: enterInfo.entered,
  };
}

export function getRaffle(raffle: I_Raffle): IResult {
  return {
    prize: {
      type: 'present',
      name: raffle.name,
      winningImage: getImageUrl(raffle.winningImage, {hasAlpha: true}),
      receivedDate: formatFromServerDate(
        raffle.receivedAt,
        DEFAULT_DATE_FORMAT
      ),
    },
  };
}

type I_LuckydrawTheme = 'SLOT_MACHINE' | 'GIFT_BOX';

type I_PrizeType = 'CASH' | 'TICKET' | 'PRESENT';

type I_AbstractPrize = {
  id: number;
  type: I_PrizeType;
  quantity: number;
  scrollImage: string;
  winningImage: string;
  receivedDateTime: string;
  expiration?: {
    type: 'DURATION' | 'DATE';
    value: string;
  };
  item: {
    id: number;
    name: string;
  };
};

type I_Prize = I_AbstractPrize &
  (
    | {
        type: 'CASH';
      }
    | {
        type: 'TICKET';
        item: {
          relationship: {
            content: I_ContentMeta;
          };
        };
      }
    | {
        type: 'PRESENT';
      }
  );

type I_Result = {
  prize: I_Prize;
};

type I_AbstractLuckydrawReward = {
  rewardId: number;
  expired: boolean;
  luckyDraw: {
    id: number;
    theme: I_LuckydrawTheme;
    title: string;
    prizes: I_Prize[];
  };
};

type I_LuckydrawReward = I_AbstractLuckydrawReward &
  (
    | {
        received: false;
      }
    | {
        received: true;
        result: {prize: I_Prize; forms: I_Form[]};
      }
  );

type I_Form = {
  key: string;
  name: string;
};

type I_EnterInfo = {
  expiresDateTime: string;
  expired: boolean;
  entered: boolean;
  forms: I_Form[];
  result?: {
    enteredForms: Array<I_Form & {value: string}>;
    lastUpdatedDateTime: string;
  };
};

type I_Raffle = {
  winningImage: string;
  name: string;
  receivedAt: string;
};
